import { Note, NoteName } from "../types/sound";

export const NOTE_NAMES: NoteName[] = [
    "C",
    "C#",
    "D",
    "D#",
    "E",
    "F",
    "F#",
    "G",
    "G#",
    "A",
    "A#",
    "B",
];

export function parseNote(note: Note): { noteName: NoteName; octave: number; midiNumber: number } {
    const regex = /^([A-G]#?)(\d+)$/;
    const match = note.match(regex);

    if (!match) {
        throw new Error(`Invalid note format: ${note}`);
    }

    const noteName = match[1] as NoteName;
    const octave = parseInt(match[2], 10);

    if (!NOTE_NAMES.includes(noteName)) {
        throw new Error(`Invalid note name: ${noteName}`);
    }

    const noteIndex = NOTE_NAMES.indexOf(noteName);
    const midiNumber = (octave + 1) * 12 + noteIndex;

    return { noteName, octave, midiNumber };
}

export function midiToNote(midiNumber: number): Note {
    const octave = Math.floor(midiNumber / 12) - 1;
    const noteIndex = midiNumber % 12;
    const noteName = NOTE_NAMES[noteIndex];
    return `${noteName}${octave}` as Note;
}

export function noteToMidi(note: Note): number {
    return parseNote(note).midiNumber;
}

export function getInterval(note1: Note, note2: Note): number {
    const midi1 = noteToMidi(note1);
    const midi2 = noteToMidi(note2);
    return Math.abs(midi2 - midi1);
}

export function transposeNote(note: Note, semitones: number): Note {
    const midi = noteToMidi(note);
    const transposedMidi = midi + semitones;

    if (transposedMidi < 0 || transposedMidi > 127) {
        throw new Error(`Transposition out of MIDI range: ${transposedMidi}`);
    }

    return midiToNote(transposedMidi);
}

export function isValidNote(note: string): note is Note {
    try {
        parseNote(note as Note);
        return true;
    } catch {
        return false;
    }
}

export function getNotesInRange(startNote: Note, endNote: Note): Note[] {
    const startMidi = noteToMidi(startNote);
    const endMidi = noteToMidi(endNote);
    const notes: Note[] = [];

    const min = Math.min(startMidi, endMidi);
    const max = Math.max(startMidi, endMidi);

    for (let midi = min; midi <= max; midi++) {
        notes.push(midiToNote(midi));
    }

    return notes;
}

export function getRandomNote(minNote: Note, maxNote: Note): Note {
    const minMidi = noteToMidi(minNote);
    const maxMidi = noteToMidi(maxNote);
    const randomMidi = Math.floor(Math.random() * (maxMidi - minMidi + 1)) + minMidi;
    return midiToNote(randomMidi);
}

export function sortNotes(notes: Note[]): Note[] {
    return notes.sort((a, b) => noteToMidi(a) - noteToMidi(b));
}
